'use client';

import {
  Settings,
  Sparkles,
  FolderKanban,
  Factory,
  FileText,
  Package,
  LayoutDashboard,
  LayoutGrid,
  User,
  HelpCircle,
  BookOpen,
  FlaskConical,
  Target,
  Bot,
  ClipboardPen,
  MessageSquare,
  Home,
  Compass,
  OctagonAlert,
  DollarSign,
  TrendingUp,
  BarChart3,
  Landmark,
  Search,
  Wand2,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useLayoutStore, type ExplorerView } from '@/lib/stores/layout-store';
import { switchExplorerMode } from '@/lib/stores/tab-store';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { getDrawerItems } from '@/lib/registry';
import { useNavItems } from '@/lib/stores/total-matrix-store';
import { testIds } from '@/lib/test-utils/selectors';

// Icon map: converts string icon names from Matrix to Lucide components
const ICON_MAP: Record<string, LucideIcon> = {
  Home,
  Sparkles,
  FolderKanban,
  Factory,
  FileText,
  Package,
  LayoutDashboard,
  LayoutGrid,
  User,
  Settings,
  BookOpen,
  FlaskConical,
  Target,
  Bot,
  ClipboardPen,
  MessageSquare,
  Compass,
  // Cohabitation marker — legacy modes whose cards-everywhere
  // replacement is now the recommended path. Still active.
  OctagonAlert,
  // FinOps (mode6) and other analytical modes.
  DollarSign,
  TrendingUp,
  BarChart3,
  // Investor Relations (mode10), Product Search (mode11), agentic
  // ask demo (mode8).
  Landmark,
  Search,
  Wand2,
};

function getIcon(iconName: string): LucideIcon {
  return ICON_MAP[iconName] || HelpCircle;
}

export function ActivityBar() {
  const { activeExplorerView } = useLayoutStore();

  // Get nav items from Matrix (role-filtered by backend) + drawer items from registry
  const navItems = useNavItems();
  const drawers = getDrawerItems();

  // The clearing logic (same-mode re-click resets to landing; a tab/
  // selection whose type isn't valid in the destination mode gets cleared;
  // otherwise it's preserved) lives in one place — switchExplorerMode —
  // since every "jump to mode X" affordance in the app needs it, not just
  // this activity bar. See its own doc comment in tab-store.ts.
  const handleModeSwitch = (mode: ExplorerView) => switchExplorerMode(mode);

  return (
    <TooltipProvider delayDuration={0}>
      <div
        data-testid={testIds.shell.activityBar}
        className="w-12 bg-activity-bar flex flex-col items-center py-2 border-r border-border"
      >
        {/* Main Activities - from Matrix (role-filtered) */}
        <div className="flex flex-col gap-1">
          {navItems.map((item) => {
            const Icon = getIcon(item.icon);
            // Stable Playwright selector — matrix is the authority.
            // `item.test_id` is server-populated for registry-driven
            // (modeN) nav items only; legacy inline items have null
            // and render no test attrs. See
            // klyde_testing/docs/canvas_testability.md.
            const activityTestId = item.test_id ?? undefined;
            return (
              <Tooltip key={item.mode}>
                <TooltipTrigger asChild>
                  <button
                    data-testid={activityTestId}
                    data-nav-mode={item.test_id ? item.mode : undefined}
                    aria-label={item.test_id ? `Navigate to ${item.label}` : undefined}
                    onClick={() => handleModeSwitch(item.mode)}
                    className={cn(
                      'w-12 h-12 flex items-center justify-center transition-colors',
                      'hover:text-activity-bar-foreground',
                      activeExplorerView === item.mode
                        ? 'text-activity-bar-foreground border-l-2 border-primary bg-activity-bar/50'
                        : 'text-activity-bar-foreground/60'
                    )}
                  >
                    <Icon className="w-6 h-6" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="flex items-center gap-2">
                  <span>{item.label}</span>
                  {item.shortcut && (
                    <kbd className="text-xs bg-muted px-1 rounded">
                      {item.shortcut}
                    </kbd>
                  )}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Drawer items from registry (notifications, quick-actions, …).
            Cart was here historically; relocated to the right-edge
            strip as the Composer icon. The DRAWER_REGISTRY is empty
            today; this loop renders nothing until a future drawer
            (notifications, etc.) registers. */}
        {drawers.map((drawer) => {
          const Icon = drawer.icon;
          const drawerTestId = testIds.activityBar[drawer.id as keyof typeof testIds.activityBar];

          return (
            <Tooltip key={drawer.id}>
              <TooltipTrigger asChild>
                <button
                  data-testid={drawerTestId}
                  className={cn(
                    'w-12 h-12 flex items-center justify-center transition-colors relative',
                    'hover:text-activity-bar-foreground text-activity-bar-foreground/60',
                  )}
                >
                  <Icon className="w-6 h-6" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{drawer.label}</TooltipContent>
            </Tooltip>
          );
        })}

        {/* Settings at bottom */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              data-testid={testIds.activityBar.settings}
              onClick={() => handleModeSwitch('settings')}
              className={cn(
                'w-12 h-12 flex items-center justify-center transition-colors',
                'hover:text-activity-bar-foreground',
                activeExplorerView === 'settings'
                  ? 'text-activity-bar-foreground'
                  : 'text-activity-bar-foreground/60'
              )}
            >
              <Settings className="w-6 h-6" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">Settings</TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
